import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Eye,
  EyeOff,
  GripVertical,
  X,
  FileText,
  RotateCw,
  RotateCcw,
  Square,
  Crop,
  ScanSearch,
  Copy,
  Scissors,
  Trash2,
  Maximize2,
  Highlighter,
  PenLine,
  StickyNote,
  Sparkles,
} from 'lucide-react'
import {
  useProjectStore,
  findDocumentById,
} from '../store/projectStore.js'
import { renderPageToDataURL } from '../lib/pdfjs.js'
import { truncate } from '../lib/format.js'

const thumbnailCache = new Map()

const DRAW_TOOLS = new Set(['redact', 'crop', 'highlight', 'signature'])
const INK_TOOL = 'ink'

export default function PageThumbnail({ page, index, isDragging, dragHandleProps }) {
  const togglePageIncluded = useProjectStore((s) => s.togglePageIncluded)
  const selectPage = useProjectStore((s) => s.selectPage)
  const selectedPageId = useProjectStore((s) => s.selectedPageId)
  const toolMode = useProjectStore((s) => s.toolMode)
  const stampSettings = useProjectStore((s) => s.stampSettings)
  const annotationSettings = useProjectStore((s) => s.annotationSettings)
  const addStampToPage = useProjectStore((s) => s.addStampToPage)
  const removeStamp = useProjectStore((s) => s.removeStamp)
  const addRedaction = useProjectStore((s) => s.addRedaction)
  const removeRedaction = useProjectStore((s) => s.removeRedaction)
  const setCrop = useProjectStore((s) => s.setCrop)
  const clearCrop = useProjectStore((s) => s.clearCrop)
  const rotatePage = useProjectStore((s) => s.rotatePage)
  const addAnnotation = useProjectStore((s) => s.addAnnotation)
  const removeAnnotation = useProjectStore((s) => s.removeAnnotation)
  const duplicatePage = useProjectStore((s) => s.duplicatePage)
  const splitPage = useProjectStore((s) => s.splitPage)
  const removePage = useProjectStore((s) => s.removePage)
  const openViewer = useProjectStore((s) => s.openViewer)
  const documents = useProjectStore((s) => s.documents)
  const signatures = useProjectStore((s) => s.signatures)
  const showToast = useProjectStore((s) => s.showToast)
  const doc = useMemo(
    () => findDocumentById(documents, page.documentId),
    [documents, page.documentId]
  )

  const [thumbUrl, setThumbUrl] = useState(() => thumbnailCache.get(page.id) || null)
  const [loadError, setLoadError] = useState(false)
  const [drawRect, setDrawRect] = useState(null)
  const [inkStroke, setInkStroke] = useState(null) // {color,size,points:[{x,y}]}
  const surfaceRef = useRef(null)
  const dragStart = useRef(null)
  const isSelected = selectedPageId === page.id

  useEffect(() => {
    let cancelled = false
    const cached = thumbnailCache.get(page.id)
    if (cached) {
      setThumbUrl(cached)
      return
    }
    if (!doc) return
    async function run() {
      try {
        const result = await renderPageToDataURL(
          doc.id,
          doc.bytes,
          page.originalIndex + 1,
          { maxWidth: 360 }
        )
        if (!cancelled) {
          thumbnailCache.set(page.id, result.dataUrl)
          setThumbUrl(result.dataUrl)
        }
      } catch (err) {
        if (!cancelled) setLoadError(true)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [doc, page.id, page.originalIndex])

  // -- pointer / click handling --------------------------------------------

  function pointToRatio(e) {
    const rect = surfaceRef.current.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    }
  }

  function handlePointerDown(e) {
    if (e.button !== 0) return
    if (toolMode === 'select' || toolMode === 'stamp' || toolMode === 'sticky') return

    e.preventDefault()
    surfaceRef.current.setPointerCapture(e.pointerId)
    const start = pointToRatio(e)
    selectPage(page.id)

    if (toolMode === INK_TOOL) {
      setInkStroke({
        color: annotationSettings.inkColor,
        size: annotationSettings.inkSize,
        points: [start],
      })
      return
    }

    if (DRAW_TOOLS.has(toolMode)) {
      dragStart.current = start
      setDrawRect({ x: start.x, y: start.y, w: 0, h: 0 })
    }
  }

  function handlePointerMove(e) {
    if (toolMode === INK_TOOL && inkStroke) {
      const p = pointToRatio(e)
      setInkStroke((s) => ({ ...s, points: [...s.points, p] }))
      return
    }
    if (!dragStart.current) return
    const p = pointToRatio(e)
    const start = dragStart.current
    setDrawRect({
      x: Math.min(start.x, p.x),
      y: Math.min(start.y, p.y),
      w: Math.abs(p.x - start.x),
      h: Math.abs(p.y - start.y),
    })
  }

  function handlePointerUp(e) {
    surfaceRef.current.releasePointerCapture?.(e.pointerId)

    if (toolMode === INK_TOOL && inkStroke) {
      if (inkStroke.points.length > 1) {
        addAnnotation(page.id, { type: 'ink', ...inkStroke })
      }
      setInkStroke(null)
      return
    }

    if (!dragStart.current) return
    const rect = drawRect
    dragStart.current = null
    setDrawRect(null)
    if (!rect || rect.w < 0.005 || rect.h < 0.005) return

    if (toolMode === 'redact') {
      addRedaction(page.id, {
        xRatio: rect.x,
        yRatio: rect.y,
        wRatio: rect.w,
        hRatio: rect.h,
      })
    } else if (toolMode === 'crop') {
      setCrop(page.id, {
        xRatio: rect.x,
        yRatio: rect.y,
        wRatio: rect.w,
        hRatio: rect.h,
      })
    } else if (toolMode === 'highlight') {
      addAnnotation(page.id, {
        type: 'highlight',
        xRatio: rect.x,
        yRatio: rect.y,
        wRatio: rect.w,
        hRatio: rect.h,
        color: annotationSettings.highlightColor,
      })
    } else if (toolMode === 'signature') {
      const sigId = annotationSettings.signatureId
      if (!sigId) {
        showToast('Pick a signature in the toolbar first (Inspector → Signatures).', 'warning')
        return
      }
      addAnnotation(page.id, {
        type: 'signature',
        signatureId: sigId,
        xRatio: rect.x,
        yRatio: rect.y,
        wRatio: rect.w,
        hRatio: rect.h,
      })
    }
  }

  function handleSurfaceClick(e) {
    if (dragStart.current || inkStroke) return
    if (toolMode === 'stamp') {
      const r = pointToRatio(e)
      if (!stampSettings.text || !stampSettings.text.trim()) return
      addStampToPage(page.id, {
        text: stampSettings.text,
        size: stampSettings.size,
        color: stampSettings.color,
        xRatio: r.x,
        yRatio: r.y,
      })
      return
    }
    if (toolMode === 'sticky') {
      const r = pointToRatio(e)
      const text = window.prompt('Note text:')
      if (text === null) return
      addAnnotation(page.id, {
        type: 'sticky',
        xRatio: r.x,
        yRatio: r.y,
        text: text || 'Note',
        color: annotationSettings.stickyColor,
      })
      return
    }
    if (toolMode === 'select') {
      selectPage(page.id)
    }
  }

  // OCR-aware redaction overlay clicks
  function handleOcrWordClick(word, e) {
    e.stopPropagation()
    if (toolMode !== 'redact') return
    addRedaction(page.id, {
      xRatio: word.xRatio,
      yRatio: word.yRatio,
      wRatio: word.wRatio,
      hRatio: word.hRatio,
    })
  }

  // -- derived styles ------------------------------------------------------

  const cropStyle = page.crop
    ? {
        left: `${page.crop.xRatio * 100}%`,
        top: `${page.crop.yRatio * 100}%`,
        width: `${page.crop.wRatio * 100}%`,
        height: `${page.crop.hRatio * 100}%`,
      }
    : null

  const drawStyle = drawRect
    ? {
        left: `${drawRect.x * 100}%`,
        top: `${drawRect.y * 100}%`,
        width: `${drawRect.w * 100}%`,
        height: `${drawRect.h * 100}%`,
      }
    : null

  const rotation = page.rotation || 0
  const isDrawTool = DRAW_TOOLS.has(toolMode) || toolMode === INK_TOOL
  const cursorClass = isDrawTool || toolMode === 'stamp' || toolMode === 'sticky'
    ? 'cursor-crosshair'
    : 'cursor-pointer'

  const ocrOverlayActive = toolMode === 'redact' && (page.ocrText || []).length > 0

  // signatures lookup for in-thumbnail preview rendering
  const signatureMap = useMemo(() => {
    const m = new Map()
    for (const s of signatures) m.set(s.id, s)
    return m
  }, [signatures])

  return (
    <div
      className={`group relative flex flex-col rounded-xl border bg-white p-2 transition-all dark:bg-slate-900 ${
        isSelected
          ? 'border-brand-500 ring-2 ring-brand-200 dark:ring-brand-900'
          : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'
      } ${isDragging ? 'thumb-shadow rotate-1 scale-[1.02]' : 'shadow-soft'}`}
    >
      <div className="mb-2 flex items-center justify-between gap-1">
        <button
          {...dragHandleProps}
          type="button"
          className="flex items-center gap-1 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
          title="Drag to reorder"
          aria-label="Drag handle"
        >
          <GripVertical className="h-3.5 w-3.5" />
          <span className="text-[11px] font-semibold tabular-nums text-slate-500">
            #{index + 1}
          </span>
        </button>

        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              openViewer(page.id)
            }}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            title="Open in viewer"
            aria-label="Open viewer"
          >
            <Maximize2 className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              rotatePage(page.id, -90)
            }}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            title="Rotate left"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              rotatePage(page.id, 90)
            }}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            title="Rotate right"
          >
            <RotateCw className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              togglePageIncluded(page.id)
            }}
            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold transition ${
              page.included
                ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300'
            }`}
            title={page.included ? 'Included' : 'Excluded'}
          >
            {page.included ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </button>
        </div>
      </div>

      <div
        className={`relative flex aspect-[3/4] w-full items-center justify-center overflow-hidden rounded-md bg-slate-100 dark:bg-slate-800 ${
          page.included ? '' : 'opacity-40 saturate-0'
        }`}
        onDoubleClick={() => openViewer(page.id)}
      >
        <div
          ref={surfaceRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onClick={handleSurfaceClick}
          className={`relative flex items-center justify-center select-none ${cursorClass}`}
          style={{
            aspectRatio: `${page.width} / ${page.height}`,
            maxHeight: '100%',
            maxWidth: '100%',
            width: page.width >= page.height ? '100%' : 'auto',
            height: page.width >= page.height ? 'auto' : '100%',
            touchAction: isDrawTool ? 'none' : 'manipulation',
            outline:
              toolMode === 'redact' ? '2px solid rgb(244 63 94 / 0.45)' :
              toolMode === 'crop' ? '2px dashed rgb(99 102 241 / 0.6)' :
              toolMode === 'highlight' ? '2px solid rgb(250 204 21 / 0.55)' :
              toolMode === 'ink' ? '2px dashed rgb(15 23 42 / 0.55)' :
              toolMode === 'signature' ? '2px solid rgb(34 197 94 / 0.55)' :
              toolMode === 'stamp' || toolMode === 'sticky' ? '2px solid rgb(165 180 252)' :
              'none',
            outlineOffset: '2px',
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center',
          }}
        >
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={`Page ${page.originalIndex + 1}`}
              className="pointer-events-none block h-full w-full select-none object-fill"
              draggable={false}
            />
          ) : loadError ? (
            <div className="flex flex-col items-center text-slate-400">
              <FileText className="h-6 w-6" />
              <span className="mt-1 text-[10px]">Preview unavailable</span>
            </div>
          ) : (
            <div className="flex flex-col items-center text-slate-400">
              <div className="h-5 w-5 animate-spin-slow rounded-full border-2 border-slate-300 border-t-brand-500" />
              <span className="mt-2 text-[10px]">Rendering…</span>
            </div>
          )}

          {/* Annotations: highlight (translucent rect) */}
          {(page.annotations || [])
            .filter((a) => a.type === 'highlight')
            .map((a) => (
              <div
                key={a.id}
                className="group/ann absolute"
                style={{
                  left: `${a.xRatio * 100}%`,
                  top: `${a.yRatio * 100}%`,
                  width: `${a.wRatio * 100}%`,
                  height: `${a.hRatio * 100}%`,
                  backgroundColor: a.color,
                  opacity: 0.42,
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeAnnotation(page.id, a.id)
                  }}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-rose-600 opacity-0 shadow-soft transition group-hover:opacity-100"
                  title="Remove highlight"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}

          {/* Annotations: ink — render as SVG */}
          {(page.annotations || []).filter((a) => a.type === 'ink').length > 0 && (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
            >
              {(page.annotations || [])
                .filter((a) => a.type === 'ink')
                .map((a) => {
                  if (!a.points || a.points.length < 2) return null
                  const d =
                    'M ' +
                    a.points.map((p) => `${p.x} ${p.y}`).join(' L ')
                  return (
                    <path
                      key={a.id}
                      d={d}
                      fill="none"
                      stroke={a.color}
                      strokeWidth={a.size / 200}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  )
                })}
              {/* live ink stroke being drawn */}
              {inkStroke && inkStroke.points.length > 1 && (
                <path
                  d={'M ' + inkStroke.points.map((p) => `${p.x} ${p.y}`).join(' L ')}
                  fill="none"
                  stroke={inkStroke.color}
                  strokeWidth={inkStroke.size / 200}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </svg>
          )}

          {/* Annotations: sticky note marker */}
          {(page.annotations || [])
            .filter((a) => a.type === 'sticky')
            .map((a) => (
              <div
                key={a.id}
                className="absolute flex items-start gap-1"
                style={{
                  left: `${a.xRatio * 100}%`,
                  top: `${a.yRatio * 100}%`,
                  transform: 'translate(0, -100%)',
                }}
              >
                <div
                  className="rounded-sm border border-amber-500 p-0.5 shadow-soft"
                  style={{ backgroundColor: a.color }}
                  title={a.text}
                >
                  <StickyNote className="h-2.5 w-2.5 text-amber-900" />
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeAnnotation(page.id, a.id)
                  }}
                  className="rounded-full bg-white p-0.5 text-rose-600 opacity-0 shadow-soft transition group-hover:opacity-100"
                  title="Remove note"
                >
                  <X className="h-2 w-2" />
                </button>
              </div>
            ))}

          {/* Annotations: signature preview */}
          {(page.annotations || [])
            .filter((a) => a.type === 'signature')
            .map((a) => {
              const sig = signatureMap.get(a.signatureId)
              return (
                <div
                  key={a.id}
                  className="absolute"
                  style={{
                    left: `${a.xRatio * 100}%`,
                    top: `${a.yRatio * 100}%`,
                    width: `${a.wRatio * 100}%`,
                    height: `${a.hRatio * 100}%`,
                  }}
                >
                  {sig && (
                    <img
                      src={sig.dataUrl}
                      alt={sig.name}
                      className="pointer-events-none h-full w-full select-none object-contain"
                      draggable={false}
                    />
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeAnnotation(page.id, a.id)
                    }}
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-rose-600 opacity-0 shadow-soft transition group-hover:opacity-100"
                    title="Remove signature"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              )
            })}

          {/* OCR words clickable when redact tool is active */}
          {ocrOverlayActive &&
            (page.ocrText || []).map((w, i) => (
              <button
                key={`${i}-${w.xRatio.toFixed(3)}`}
                type="button"
                onClick={(e) => handleOcrWordClick(w, e)}
                title={`Click to redact "${w.text}"`}
                className="absolute border border-rose-400/60 bg-rose-100/30 hover:bg-rose-300/60"
                style={{
                  left: `${w.xRatio * 100}%`,
                  top: `${w.yRatio * 100}%`,
                  width: `${w.wRatio * 100}%`,
                  height: `${w.hRatio * 100}%`,
                  pointerEvents: 'auto',
                }}
              />
            ))}

          {/* Redactions (drawn last under overlays so they're always visible) */}
          {(page.redactions || []).map((r) => (
            <div
              key={r.id}
              className="absolute bg-black"
              style={{
                left: `${r.xRatio * 100}%`,
                top: `${r.yRatio * 100}%`,
                width: `${r.wRatio * 100}%`,
                height: `${r.hRatio * 100}%`,
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  removeRedaction(page.id, r.id)
                }}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-rose-600 opacity-0 shadow-soft transition group-hover:opacity-100"
                title="Remove redaction"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}

          {/* Crop preview */}
          {cropStyle && (
            <>
              <div className="pointer-events-none absolute inset-0 bg-slate-900/35" />
              <div
                className="absolute border-2 border-brand-400 bg-transparent"
                style={cropStyle}
              >
                <span className="absolute -top-5 left-0 rounded bg-brand-600 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                  Crop
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    clearCrop(page.id)
                  }}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-rose-600 shadow-soft"
                  title="Remove crop"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            </>
          )}

          {/* Active draw rectangle */}
          {drawStyle && (
            <div
              className={`pointer-events-none absolute border-2 ${
                toolMode === 'redact'
                  ? 'border-rose-500 bg-rose-500/40'
                  : toolMode === 'highlight'
                  ? 'border-yellow-500 bg-yellow-500/30'
                  : toolMode === 'signature'
                  ? 'border-emerald-500 bg-emerald-500/20'
                  : 'border-brand-500 bg-brand-500/20'
              }`}
              style={drawStyle}
            />
          )}

          {/* Stamps centered on click */}
          {(page.stamps || []).map((stamp) => (
            <div
              key={stamp.id}
              className="pointer-events-none absolute"
              style={{
                left: `${stamp.xRatio * 100}%`,
                top: `${stamp.yRatio * 100}%`,
                transform: 'translate(-50%, -50%)',
                color: stamp.color,
                fontSize: `${Math.max(8, stamp.size * 0.55)}px`,
                fontFamily: 'Helvetica, Arial, sans-serif',
                whiteSpace: 'nowrap',
              }}
            >
              <span className="relative inline-flex items-center gap-1 rounded-sm bg-white/40 px-1 backdrop-blur-[1px]">
                {stamp.text}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeStamp(page.id, stamp.id)
                  }}
                  className="pointer-events-auto rounded-full bg-white/90 p-0.5 text-rose-600 opacity-0 shadow-soft transition group-hover:opacity-100"
                  title="Remove stamp"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            </div>
          ))}

          {/* OCR applied badge */}
          {page.ocrApplied && (page.ocrText || []).length > 0 && (
            <div className="pointer-events-none absolute bottom-1 left-1 rounded-full bg-indigo-600/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
              <span className="inline-flex items-center gap-0.5">
                <ScanSearch className="h-2.5 w-2.5" />
                OCR
              </span>
            </div>
          )}
        </div>

        {!page.included && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-slate-900/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
              Excluded
            </span>
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 px-0.5">
        <p
          className="truncate text-[11px] text-slate-500 dark:text-slate-400"
          title={page.documentName}
        >
          {truncate(page.documentName, 22)}
        </p>
        <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
          {(page.annotations || []).length > 0 && (
            <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1 py-px text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
              <Highlighter className="h-2.5 w-2.5" />
              {page.annotations.length}
            </span>
          )}
          {(page.redactions || []).length > 0 && (
            <span className="inline-flex items-center gap-0.5 rounded bg-rose-50 px-1 py-px text-rose-700 dark:bg-rose-900/30 dark:text-rose-200">
              <Square className="h-2.5 w-2.5" />
              {page.redactions.length}
            </span>
          )}
          {page.crop && (
            <span className="inline-flex items-center gap-0.5 rounded bg-brand-50 px-1 py-px text-brand-700 dark:bg-brand-900/30 dark:text-brand-200">
              <Crop className="h-2.5 w-2.5" />
            </span>
          )}
          {rotation !== 0 && (
            <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1 py-px text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
              <RotateCw className="h-2.5 w-2.5" />
              {rotation}°
            </span>
          )}
          <span>p.{page.originalIndex + 1}</span>
        </div>
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-1 px-0.5 opacity-0 transition group-hover:opacity-100">
        <div className="flex items-center gap-0.5">
          <button
            onClick={(e) => {
              e.stopPropagation()
              duplicatePage(page.id)
            }}
            className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            title="Duplicate page"
          >
            <Copy className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              const axis = window.confirm(
                'Split horizontally? Press Cancel to split vertically.'
              )
                ? 'horizontal'
                : 'vertical'
              splitPage(page.id, axis)
            }}
            className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            title="Split page"
          >
            <Scissors className="h-3 w-3" />
          </button>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            if (window.confirm('Remove this page entirely?')) removePage(page.id)
          }}
          className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/30"
          title="Delete page"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}
